import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SunIcon, MoonIcon, ChartBarIcon, UserIcon, LockClosedIcon, CogIcon, CheckCircleIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../contexts/AuthContext';
import lockService from '../../services/lockService';
import OnlineUsers from '../OnlineUsers';
import { websiteService, backupService, probeService } from '../../services/api';

const Header: React.FC = () => {
  const { darkMode, toggleDarkMode } = useTheme();
  const { user, logout } = useAuth();
  const [isReportsMenuOpen, setIsReportsMenuOpen] = useState(false);
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isOnlineUsersOpen, setIsOnlineUsersOpen] = useState(false);
  const [activeLocks, setActiveLocks] = useState<Record<string, any>>({});
  const [refreshKey, setRefreshKey] = useState(0); // Pour forcer le re-render
  
  // États pour les actions
  const [checkingAllSites, setCheckingAllSites] = useState(false);
  const [checkingAllBackups, setCheckingAllBackups] = useState(false);
  const [detectingAllCMS, setDetectingAllCMS] = useState(false);
  const [clearingMainWPCache, setClearingMainWPCache] = useState(false);
  const navigate = useNavigate();
  
  const reportsMenuRef = useRef<HTMLDivElement>(null);
  const reportsButtonRef = useRef<HTMLButtonElement>(null);
  const reportsMenuContainerRef = useRef<HTMLDivElement>(null);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const actionsButtonRef = useRef<HTMLButtonElement>(null);
  const actionsMenuContainerRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userButtonRef = useRef<HTMLButtonElement>(null);
  const onlineUsersMenuRef = useRef<HTMLDivElement>(null);
  const onlineUsersButtonRef = useRef<HTMLButtonElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Charger les verrous actifs
  useEffect(() => {
    const loadActiveLocks = async () => {
      try {
        const locks = await lockService.getActiveLocks();
        setActiveLocks(locks);
      } catch (error) {
        console.error('Erreur lors du chargement des verrous actifs:', error);
      }
    };

    loadActiveLocks();
    
    // Rafraîchir les verrous toutes les 15 secondes (plus fréquent)
    const interval = setInterval(loadActiveLocks, 15000);
    
    return () => clearInterval(interval);
  }, []);

  // Forcer la mise à jour du composant OnlineUsers
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshKey(prev => prev + 1);
    }, 5000); // Toutes les 5 secondes
    
    return () => clearInterval(interval);
  }, []);

  // Gérer les clics en dehors des menus pour les fermer
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        reportsMenuRef.current && 
        !reportsMenuRef.current.contains(event.target as Node) &&
        reportsButtonRef.current && 
        !reportsButtonRef.current.contains(event.target as Node)
      ) {
        setIsReportsMenuOpen(false);
      }
      
      if (
        actionsMenuRef.current && 
        !actionsMenuRef.current.contains(event.target as Node) &&
        actionsButtonRef.current && 
        !actionsButtonRef.current.contains(event.target as Node)
      ) {
        setIsActionsMenuOpen(false);
      }
      
      if (
        userMenuRef.current && 
        !userMenuRef.current.contains(event.target as Node) &&
        userButtonRef.current && 
        !userButtonRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }

      if (
        onlineUsersMenuRef.current && 
        !onlineUsersMenuRef.current.contains(event.target as Node) &&
        onlineUsersButtonRef.current && 
        !onlineUsersButtonRef.current.contains(event.target as Node)
      ) {
        setIsOnlineUsersOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Nettoyer le timeout lorsque le composant est démonté
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Fonction pour ouvrir le menu
  const handleMenuMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsReportsMenuOpen(true);
  };

  // Fonction pour fermer le menu avec un délai
  const handleMenuMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsReportsMenuOpen(false);
    }, 300); // Délai de 300ms avant fermeture
  };

  // Fonction de déconnexion
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Fonctions pour les actions
  const handleCheckAllSites = async () => {
    try {
      setCheckingAllSites(true);
      setIsActionsMenuOpen(false);
      await websiteService.checkAllStatus();
      alert('Vérification de tous les sites terminée !');
    } catch (error) {
      console.error('Erreur lors de la vérification des sites:', error);
      alert('Erreur lors de la vérification des sites.');
    } finally {
      setCheckingAllSites(false);
    }
  };

  const handleCheckAllBackups = async () => {
    try {
      setCheckingAllBackups(true);
      setIsActionsMenuOpen(false);
      await backupService.checkAllBackups();
      alert('Vérification de tous les backups terminée !');
    } catch (error) {
      console.error('Erreur lors de la vérification des backups:', error);
      alert('Erreur lors de la vérification des backups.');
    } finally {
      setCheckingAllBackups(false);
    }
  };

  const handleDetectAllMainWP = async () => {
    try {
      setDetectingAllCMS(true);
      setIsActionsMenuOpen(false);
      await probeService.detectProbeForAllWebsites('wordpress');
      alert('Détection MainWP sur tous les sites terminée !');
    } catch (error) {
      console.error('Erreur lors de la détection MainWP:', error);
      alert('Erreur lors de la détection MainWP.');
    } finally {
      setDetectingAllCMS(false);
    }
  };

  const handleClearMainWPCache = async () => {
    try {
      setClearingMainWPCache(true);
      setIsActionsMenuOpen(false);
      const result = await probeService.clearMainWPCache();
      
      if (result.success) {
        alert('Cache MainWP vidé avec succès. Les prochaines détections utiliseront des données fraîches.');
      } else {
        alert(`Erreur lors du vidage du cache MainWP: ${result.message}`);
      }
    } catch (error) {
      console.error('Erreur lors du vidage du cache MainWP:', error);
      alert('Erreur lors du vidage du cache MainWP. Veuillez réessayer plus tard.');
    } finally {
      setClearingMainWPCache(false);
    }
  };

  const activeLocksCount = Object.keys(activeLocks).length;

  return (
    <header className="bg-white dark:bg-gray-800 shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          <div className="flex items-center">
            <Link to="/" className="text-2xl font-bold text-primary-600 dark:text-primary-400">
              CC Gestion
            </Link>
          </div>
          
          <div className="flex items-center space-x-4">
            <nav className="hidden md:flex space-x-4">
              <Link to="/" className="px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700">
                Tableau de bord
              </Link>
              <Link to="/sites" className="px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700">
                Sites Web
              </Link>
              <div 
                className="relative" 
                ref={reportsMenuContainerRef}
                onMouseEnter={handleMenuMouseEnter}
                onMouseLeave={handleMenuMouseLeave}
              >
                <button 
                  ref={reportsButtonRef}
                  className="px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                  onClick={() => setIsReportsMenuOpen(!isReportsMenuOpen)}
                >
                  <ChartBarIcon className="h-4 w-4 mr-1" />
                  Rapports
                  <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isReportsMenuOpen && (
                  <div 
                    ref={reportsMenuRef}
                    className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-10"
                  >
                  <div className="py-1">
                      <Link 
                        to="/billing" 
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => setIsReportsMenuOpen(false)}
                      >
                        Facturation
                      </Link>
                      <Link 
                        to="/reports/mainwp" 
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => setIsReportsMenuOpen(false)}
                      >
                      Sites MainWP non référencés
                    </Link>
                      <Link 
                        to="/reports/minio" 
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => setIsReportsMenuOpen(false)}
                      >
                      Rapport buckets Minio
                    </Link>
                      <Link 
                        to="/reports/backup-slots" 
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => setIsReportsMenuOpen(false)}
                      >
                      Rapport tranches horaires
                    </Link>
                      <Link 
                        to="/backups/management" 
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => setIsReportsMenuOpen(false)}
                      >
                      Gestion des sauvegardes
                    </Link>
                  </div>
                </div>
                )}
              </div>
              
              <div 
                className="relative" 
                ref={actionsMenuContainerRef}
              >
                <button 
                  ref={actionsButtonRef}
                  className="px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                  onClick={() => setIsActionsMenuOpen(!isActionsMenuOpen)}
                >
                  <CogIcon className="h-4 w-4 mr-1" />
                  Actions
                  <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isActionsMenuOpen && (
                  <div 
                    ref={actionsMenuRef}
                    className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-10"
                  >
                    <div className="py-1">
                      <button
                        onClick={handleCheckAllSites}
                        disabled={checkingAllSites}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-center">
                          {checkingAllSites ? (
                            <div className="animate-spin h-4 w-4 border-2 border-gray-600 border-t-transparent rounded-full mr-2"></div>
                          ) : (
                            <CheckCircleIcon className="h-4 w-4 mr-2" />
                          )}
                          {checkingAllSites ? 'Vérification...' : 'Vérifier sites'}
                        </div>
                      </button>
                      
                      <button
                        onClick={handleCheckAllBackups}
                        disabled={checkingAllBackups}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-center">
                          {checkingAllBackups ? (
                            <div className="animate-spin h-4 w-4 border-2 border-gray-600 border-t-transparent rounded-full mr-2"></div>
                          ) : (
                            <ArchiveBoxIcon className="h-4 w-4 mr-2" />
                          )}
                          {checkingAllBackups ? 'Vérification...' : 'Vérifier backups'}
                        </div>
                      </button>
                      
                      <button
                        onClick={handleDetectAllMainWP}
                        disabled={detectingAllCMS}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-center">
                          {detectingAllCMS ? (
                            <div className="animate-spin h-4 w-4 border-2 border-gray-600 border-t-transparent rounded-full mr-2"></div>
                          ) : (
                            <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M22 6.2c0-1.7-1.4-3.1-3.1-3.1H5.1C3.4 3.1 2 4.5 2 6.2v11.6c0 1.7 1.4 3.1 3.1 3.1h13.7c1.7 0 3.1-1.4 3.1-3.1V6.2z" />
                            </svg>
                          )}
                          {detectingAllCMS ? 'Détection...' : 'Détecter MainWP'}
                        </div>
                      </button>
                      
                      <button
                        onClick={handleClearMainWPCache}
                        disabled={clearingMainWPCache}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-center">
                          {clearingMainWPCache ? (
                            <div className="animate-spin h-4 w-4 border-2 border-gray-600 border-t-transparent rounded-full mr-2"></div>
                          ) : (
                            <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                            </svg>
                          )}
                          {clearingMainWPCache ? 'Vidage...' : 'Vider cache MainWP'}
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </nav>
            
            {/* Indicateur de verrous actifs */}
            {activeLocksCount > 0 && (
              <div className="flex items-center space-x-1 px-2 py-1 bg-yellow-100 dark:bg-yellow-900 rounded-md">
                <LockClosedIcon className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                <span className="text-xs text-yellow-800 dark:text-yellow-200">
                  {activeLocksCount} site{activeLocksCount > 1 ? 's' : ''} en cours de modification
                </span>
              </div>
            )}

            {/* Indicateur d'utilisateurs connectés */}
            <div className="relative">
              <button 
                ref={onlineUsersButtonRef}
                className="flex items-center space-x-1 px-2 py-1 bg-green-100 dark:bg-green-900 rounded-md hover:bg-green-200 dark:hover:bg-green-800 transition-colors"
                onClick={() => setIsOnlineUsersOpen(!isOnlineUsersOpen)}
              >
                <OnlineUsers key={refreshKey} showDetails={false} />
              </button>
              {isOnlineUsersOpen && (
                <div 
                  ref={onlineUsersMenuRef}
                  className="absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-10"
                >
                  <OnlineUsers key={`detail-${refreshKey}`} className="border-0 shadow-none" />
                </div>
              )}
            </div>
            
            <button 
              onClick={toggleDarkMode}
              className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label={darkMode ? 'Passer au mode clair' : 'Passer au mode sombre'}
            >
              {darkMode ? (
                <SunIcon className="h-5 w-5 text-yellow-400" />
              ) : (
                <MoonIcon className="h-5 w-5 text-gray-600" />
              )}
            </button>

            {/* Bouton de configuration système */}
            <button 
              onClick={() => navigate('/admin/system')}
              className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Configuration système"
            >
              <CogIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>

            {/* Menu utilisateur */}
            {user && (
              <div className="relative">
                {/* Indicateur de connexion */}
                <div className="absolute -top-1 -right-1 h-3 w-3 bg-green-400 border-2 border-white dark:border-gray-800 rounded-full"></div>
                
                <button 
                  ref={userButtonRef}
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                >
                  <UserIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span className="font-medium text-gray-900 dark:text-gray-100">{user.name || user.username}</span>
                  <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded font-medium">
                    {user.role}
                  </span>
                  <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isUserMenuOpen && (
                  <div 
                    ref={userMenuRef}
                    className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-10"
                  >
                    <div className="py-1">
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-600">
                        <div className="flex items-center space-x-2 mb-1">
                          <div className="h-2 w-2 bg-green-400 rounded-full"></div>
                          <span className="text-xs font-medium text-green-600 dark:text-green-400">Connecté</span>
                        </div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">{user.name}</div>
                        <div className="text-xs">@{user.username}</div>
                        <div className="text-xs">{user.email}</div>
                      </div>
                      <button 
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-red-600 dark:hover:text-red-400"
                      >
                        Se déconnecter
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 